import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import type { AppEnvironment } from "../../config/environment.js";
import { requireData } from "../api/api-error.js";
import { createTradingEngineClient } from "../api/client.js";
import type { components } from "../api/generated/trading-engine.js";
import {
  AUTH_ATTEMPT_KEY,
  AUTH_SESSION_KEY,
  authorizationUrl,
  clearStoredSession,
  exchangeAuthorizationCode,
  logoutUrl,
  readStoredSession,
  refreshSession,
  writeStoredSession,
  type OAuthConfiguration,
  type StoredSession,
} from "./oauth.js";

type Principal = components["schemas"]["Principal"];
type AuthStatus = "bootstrapping" | "authenticated" | "unauthenticated";

export interface AuthContextValue {
  readonly completeSignIn: (callbackUrl: string) => Promise<string>;
  readonly error: string | null;
  readonly expireSession: () => void;
  readonly getAccessToken: () => string | null;
  readonly principal: Principal | null;
  readonly signIn: (returnTo: string) => Promise<void>;
  readonly signOut: () => void;
  readonly status: AuthStatus;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function oauthConfiguration(environment: AppEnvironment): OAuthConfiguration {
  return {
    clientId: environment.VITE_COGNITO_CLIENT_ID,
    domain: environment.VITE_COGNITO_DOMAIN,
    logoutUri: environment.VITE_COGNITO_LOGOUT_URI,
    redirectUri: environment.VITE_COGNITO_REDIRECT_URI,
  };
}

interface AuthProviderProps extends PropsWithChildren {
  readonly attemptStorage?: Storage;
  readonly environment: AppEnvironment;
  readonly fetchImplementation?: typeof fetch;
  readonly storage?: Storage;
}

export function AuthProvider({
  attemptStorage = sessionStorage,
  children,
  environment,
  fetchImplementation = fetch,
  storage = localStorage,
}: AuthProviderProps) {
  const [session, setSession] = useState<StoredSession | null>(null);
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [status, setStatus] = useState<AuthStatus>("bootstrapping");
  const [error, setError] = useState<string | null>(null);
  const configuration = useMemo(() => oauthConfiguration(environment), [environment]);

  const expireSession = useCallback(() => {
    clearStoredSession(storage);
    clearStoredSession(attemptStorage);
    setSession(null);
    setPrincipal(null);
    setStatus("unauthenticated");
  }, [attemptStorage, storage]);

  const loadPrincipal = useCallback(
    async (activeSession: StoredSession): Promise<Principal> => {
      const client = createTradingEngineClient({
        baseUrl: environment.VITE_API_BASE_URL,
        fetchImplementation,
        getAccessToken: () => activeSession.accessToken,
        onUnauthorized: expireSession,
      });
      return requireData(await client.GET("/me"));
    },
    [environment.VITE_API_BASE_URL, expireSession, fetchImplementation],
  );

  const activate = useCallback(
    async (nextSession: StoredSession): Promise<void> => {
      const nextPrincipal = await loadPrincipal(nextSession);
      writeStoredSession(storage, nextSession);
      setSession(nextSession);
      setPrincipal(nextPrincipal);
      setError(null);
      setStatus("authenticated");
    },
    [loadPrincipal, storage],
  );

  useEffect(() => {
    let active = true;
    async function restore(): Promise<void> {
      try {
        let restored = readStoredSession(storage);
        if (restored === null) {
          if (active) setStatus("unauthenticated");
          return;
        }
        if (restored.rememberedUntil <= Date.now()) {
          throw new Error("The remembered session expired.");
        }
        if (restored.expiresAt <= Date.now() + 60_000) {
          restored = await refreshSession(configuration, restored, fetchImplementation);
        }
        if (!active) return;
        await activate(restored);
      } catch {
        if (!active) return;
        clearStoredSession(storage);
        setError("Your session expired. Sign in again to continue.");
        setStatus("unauthenticated");
      }
    }
    void restore();
    return () => {
      active = false;
    };
  }, [activate, configuration, fetchImplementation, storage]);

  useEffect(() => {
    if (session === null || status !== "authenticated") return;
    const refreshAt = Math.min(session.expiresAt - 60_000, session.rememberedUntil);
    const timeout = window.setTimeout(
      () => {
        if (session.rememberedUntil <= Date.now()) {
          expireSession();
          setError("Your 24-hour session expired. Sign in again to continue.");
          return;
        }
        void refreshSession(configuration, session, fetchImplementation)
          .then(activate)
          .catch(() => {
            expireSession();
            setError("Your session expired. Sign in again to continue.");
          });
      },
      Math.max(0, refreshAt - Date.now()),
    );
    return () => window.clearTimeout(timeout);
  }, [activate, configuration, expireSession, fetchImplementation, session, status]);

  const signIn = useCallback(
    async (returnTo: string): Promise<void> => {
      const request = await authorizationUrl(configuration, returnTo);
      attemptStorage.setItem(AUTH_ATTEMPT_KEY, JSON.stringify(request.attempt));
      window.location.assign(request.url);
    },
    [attemptStorage, configuration],
  );

  const completeSignIn = useMemo<AuthContextValue["completeSignIn"]>(() => {
    let completion: Promise<string> | null = null;
    return (callbackUrl: string): Promise<string> => {
      completion ??= (async () => {
        const result = await exchangeAuthorizationCode(
          configuration,
          callbackUrl,
          attemptStorage,
          fetchImplementation,
        );
        await activate(result.session);
        return result.returnTo;
      })();
      return completion;
    };
  }, [activate, attemptStorage, configuration, fetchImplementation]);

  const signOut = useCallback(() => {
    expireSession();
    window.location.assign(logoutUrl(configuration));
  }, [configuration, expireSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      completeSignIn,
      error,
      expireSession,
      getAccessToken: () => session?.accessToken ?? null,
      principal,
      signIn,
      signOut,
      status,
    }),
    [
      completeSignIn,
      error,
      expireSession,
      principal,
      session?.accessToken,
      signIn,
      signOut,
      status,
    ],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === null) throw new Error("useAuth must be used inside AuthProvider.");
  return context;
}

export const authStorageKeys = { attempt: AUTH_ATTEMPT_KEY, session: AUTH_SESSION_KEY } as const;
